import React, { createContext, useContext, useRef, useEffect, useState, useLayoutEffect, ReactNode } from 'react';
import { HomePage } from '@/routes/HomePage';
import { StudyPage } from '@/routes/StudyPage';
import { WordsPage } from '@/routes/WordsPage';
import { GrammarPage } from '@/routes/GrammarPage';
import { ProfilePage } from '@/routes/ProfilePage';

export const MAIN_TAB_PATHS = ['/', '/study', '/words', '/grammar', '/profile'];

export function isMainTabPath(pathname: string): boolean {
  return MAIN_TAB_PATHS.includes(pathname);
}

// Контекст активности таба
const TabActiveContext = createContext<boolean>(true);

/**
 * Хук, возвращающий true, если текущий таб видим пользователю прямо сейчас
 */
export function useIsTabActive(): boolean {
  return useContext(TabActiveContext);
}

/**
 * Хук фоновой валидации (Stale-While-Revalidate):
 * вызывает callback в фоне каждый раз, когда таб становится активным (переход на него).
 */
export function useOnTabActive(callback: () => void) {
  const isActive = useIsTabActive();
  const prevActiveRef = useRef(false);

  useEffect(() => {
    // Срабатывает при каждом переходе на таб (false -> true)
    if (isActive && !prevActiveRef.current) {
      callback();
    }
    prevActiveRef.current = isActive;
  }, [isActive, callback]);
}

interface MainTabsKeepAliveProps {
  activePath: string;
}

export function MainTabsKeepAlive({ activePath }: MainTabsKeepAliveProps) {
  // Ленивый монтаж: монтируем таб в DOM только при первом визите, после чего держим в памяти
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set([activePath]));
  const scrollPositions = useRef<Record<string, number>>({});
  const tabContainersRef = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (isMainTabPath(activePath)) {
      setVisitedTabs(prev => {
        if (prev.has(activePath)) return prev;
        const next = new Set(prev);
        next.add(activePath);
        return next;
      });
    }
  }, [activePath]);

  // Сохранение и восстановление скролла при переключении между вкладками
  const prevActiveRef = useRef(activePath);
  useLayoutEffect(() => {
    const prev = prevActiveRef.current;
    if (prev && prev !== activePath && tabContainersRef.current[prev]) {
      const scrollEl = tabContainersRef.current[prev]?.querySelector('.app-content');
      if (scrollEl) {
        scrollPositions.current[prev] = scrollEl.scrollTop;
      }
    }

    // Восстанавливаем сохранённый скролл для входящего таба
    if (activePath && tabContainersRef.current[activePath]) {
      const savedScroll = scrollPositions.current[activePath];
      if (typeof savedScroll === 'number') {
        const scrollEl = tabContainersRef.current[activePath]?.querySelector('.app-content');
        if (scrollEl) {
          scrollEl.scrollTop = savedScroll;
        }
      }
    }
    prevActiveRef.current = activePath;
  }, [activePath]);

  const tabs = [
    { path: '/', component: <HomePage /> },
    { path: '/study', component: <StudyPage /> },
    { path: '/words', component: <WordsPage /> },
    { path: '/grammar', component: <GrammarPage /> },
    { path: '/profile', component: <ProfilePage /> },
  ];

  return (
    <>
      {tabs.map(tab => {
        const hasVisited = visitedTabs.has(tab.path);
        if (!hasVisited) return null;

        const isActive = activePath === tab.path;

        return (
          <TabActiveContext.Provider key={tab.path} value={isActive}>
            <div
              ref={el => { tabContainersRef.current[tab.path] = el; }}
              data-tab-path={tab.path}
              style={{
                display: isActive ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                width: '100%',
                minHeight: '100dvh',
              }}
            >
              {tab.component}
            </div>
          </TabActiveContext.Provider>
        );
      })}
    </>
  );
}
